import { request } from '@/utils/http'

export type BusinessExceptionTab = 'MY' | 'TEAM' | 'PENDING_ASSIGN'
export type BusinessExceptionObjectType = 'DISTRIBUTION_SHIPMENT' | 'TRANSPORT_CONSIGNMENT' | 'WAYBILL' | 'FEE'
export type BusinessExceptionAction = 'ASSIGN' | 'START' | 'APPEND_RESULT' | 'RESOLVE' | 'VERIFY' | 'RETURN' | 'CLOSE'

export interface BusinessExceptionPageRequest {
  pageNum: number
  pageSize: number
  tab: BusinessExceptionTab
  keyword?: string
  status?: string
  objectType?: BusinessExceptionObjectType
  categoryCode?: string
  subcategoryCode?: string
  levelCode?: string
  assigneeId?: string
  triggeredStart?: string
  triggeredEnd?: string
  overdue?: boolean
}

export interface BusinessExceptionWorkbenchFilters {
  keyword: string
  objectType: BusinessExceptionObjectType | ''
  categoryCode: string
  subcategoryCode: string
  levelCode: string
  status: string
  assigneeId: string
  triggeredRange: string[]
  overdue: boolean | ''
}

export interface BusinessExceptionPageItem {
  id: string
  exceptionNo: string
  sourceType: string
  objectType?: BusinessExceptionObjectType
  objectNo?: string
  objectName?: string
  title: string
  categoryName?: string
  subcategoryName?: string
  levelCode: string
  status: string
  assigneeId?: string
  assigneeName?: string
  triggeredAt?: string
  slaDeadline?: string
  version: number
}

export interface BusinessExceptionPageResponse {
  records: BusinessExceptionPageItem[]
  total: number
  current: number
  size: number
}

export interface BusinessExceptionSummaryRequest extends Omit<BusinessExceptionPageRequest, 'pageNum' | 'pageSize'> {
}

export interface BusinessExceptionSummary {
  todayCreated: number | string
  pendingCount: number | string
  inProgressOverdue: number | string
  todayClosed: number | string
}

export interface BusinessExceptionSummaryCounts {
  todayCreated: number
  pendingCount: number
  inProgressOverdue: number
  todayClosed: number
}

export interface BusinessExceptionTeamMemberRequest {
  username?: string
  cursor?: string
  pageSize: number
}

export interface BusinessExceptionTeamMember {
  id: string
  username: string
  realName?: string
}

export interface BusinessExceptionTeamMemberPage {
  records: BusinessExceptionTeamMember[]
  nextCursor?: string
  hasMore: boolean
}

export interface BusinessExceptionBatchAssignItem {
  exceptionId: string
  version: number
}

export interface BusinessExceptionBatchAssignRequest {
  items: BusinessExceptionBatchAssignItem[]
  assigneeId: string
}

export interface BusinessExceptionBatchAssignResult {
  exceptionId: string
  success: boolean
  errorCode?: string
  detail?: BusinessExceptionDetail
}

export interface BusinessExceptionObjectSearchRequest {
  objectType: BusinessExceptionObjectType
  keyword?: string
  pageSize?: number
  cursor?: string
}

export interface BusinessExceptionObjectSnapshot {
  objectId: string
  objectType: BusinessExceptionObjectType
  objectNo?: string
  objectName?: string
  snapshot?: Record<string, unknown>
}

export interface BusinessExceptionObjectPageResponse {
  records: BusinessExceptionObjectSnapshot[]
  nextCursor?: string
  hasMore: boolean
}

export interface BusinessExceptionDetail extends BusinessExceptionPageItem {
  description: string
  ruleCode?: string
  ruleName?: string
  distributionShipmentNo?: string
  transportConsignmentNo?: string
  waybillNo?: string
  feeName?: string
  businessType?: string
  supplierName?: string
  routeName?: string
  triggerCondition?: string
  assignedAt?: string
  startedAt?: string
  resolvedAt?: string
  closedAt?: string
  closeReason?: string
  slaHours?: number
  availableActions: BusinessExceptionAction[]
  objects: BusinessExceptionObjectSnapshot[]
  histories: BusinessExceptionHistory[]
}

export interface BusinessExceptionHistory {
  id: string
  actionType: string
  fromStatus?: string
  toStatus?: string
  operatorId?: string
  operatorName?: string
  content?: string
  dateCreated?: string
}

export interface BusinessExceptionManualObject {
  objectType: BusinessExceptionObjectType
  objectId: string
}

export interface BusinessExceptionManualCreateRequest {
  categoryCode: string
  subcategoryCode: string
  levelCode: string
  objects: BusinessExceptionManualObject[]
  description: string
  assigneeId?: string
}

export interface BusinessExceptionCategory {
  id: string
  parentId: string
  categoryCode: string
  categoryName: string
  categoryLevel: number
  sortNo: number
  enabled: boolean
  children: BusinessExceptionCategory[]
}

export interface BusinessExceptionCategorySaveRequest {
  id?: string
  parentId?: string
  categoryCode: string
  categoryName: string
  sortNo?: number
  enabled?: boolean
}

export interface BusinessExceptionLevel {
  id: string
  levelCode: string
  levelName: string
  severity: number
  slaHours: number
  color?: string
  enabled: boolean
}

export interface BusinessExceptionLevelSaveRequest {
  id?: string
  levelCode: string
  levelName: string
  severity: number
  slaHours: number
  color?: string
  enabled?: boolean
}

export type BusinessExceptionLevelRuleConditionMode = 'ALL' | 'ANY' | 'NONE'
export type BusinessExceptionLevelRuleDimension = 'DELAY_HOURS' | 'CUSTOMER_TIER' | 'BUSINESS_TYPE'
export type BusinessExceptionLevelRuleOperator = 'GTE' | 'GT' | 'EQ' | 'LT' | 'LTE' | 'BETWEEN'

export interface BusinessExceptionLevelRuleCondition {
  dimension: BusinessExceptionLevelRuleDimension
  operator: BusinessExceptionLevelRuleOperator
  value?: string | number
  valueEnd?: number
}

export interface BusinessExceptionLevelRuleVO {
  id: string
  ruleName: string
  priority: number
  categoryCode?: string
  subcategoryCode?: string
  conditionMode: BusinessExceptionLevelRuleConditionMode
  conditions: BusinessExceptionLevelRuleCondition[]
  levelCode: string
  deductScore: number
  assigneeIds: string[]
  assignees: BusinessExceptionTeamMember[]
  enabled: boolean
}

export interface BusinessExceptionLevelRuleSaveRequest extends Omit<BusinessExceptionLevelRuleVO, 'id' | 'assignees'> {
  id?: string
}

export interface BusinessExceptionOperationRequest {
  exceptionId: string
  version: number
  assigneeId?: string
  content?: string
}
export function pageBusinessExceptions(data: BusinessExceptionPageRequest) {
  return request.post<BusinessExceptionPageResponse>('/sys/business-exception/page', data)
}

export function summaryBusinessExceptions(data: BusinessExceptionSummaryRequest) {
  return request.post<BusinessExceptionSummary>('/sys/business-exception/summary', data)
}
export function searchBusinessExceptionTeamMembers(data: BusinessExceptionTeamMemberRequest) {
  return request.post<BusinessExceptionTeamMemberPage>('/sys/business-exception/team-members', data, { timeout: 15000 })
}

export function searchBusinessExceptionObjects(data: BusinessExceptionObjectSearchRequest) {
  return request.post<BusinessExceptionObjectPageResponse>('/sys/business-exception/object/search', data)
}

export function createBusinessException(data: BusinessExceptionManualCreateRequest) {
  return request.post<{ exceptionId: string; exceptionNo: string; status: string }>('/sys/business-exception/manual-create', data)
}

export function getBusinessExceptionDetail(exceptionId: string) {
  return request.post<BusinessExceptionDetail>('/sys/business-exception/detail', { exceptionId })
}

export function listBusinessExceptionCategories() {
  return request.post<BusinessExceptionCategory[]>('/sys/business-exception/basic-data/category/tree', {})
}

export function saveBusinessExceptionCategory(data: BusinessExceptionCategorySaveRequest) {
  return request.post<BusinessExceptionCategory>('/sys/business-exception/basic-data/category/save', data)
}

export function deleteBusinessExceptionCategory(id: string) {
  return request.post<boolean>('/sys/business-exception/basic-data/category/delete', { id })
}

export function listBusinessExceptionLevels() {
  return request.post<BusinessExceptionLevel[]>('/sys/business-exception/basic-data/level/list', {})
}

export function saveBusinessExceptionLevel(data: BusinessExceptionLevelSaveRequest) {
  return request.post<BusinessExceptionLevel>('/sys/business-exception/basic-data/level/save', data)
}

export function deleteBusinessExceptionLevel(id: string) {
  return request.post<boolean>('/sys/business-exception/basic-data/level/delete', { id })
}

export function listBusinessExceptionLevelRules() {
  return request.post<BusinessExceptionLevelRuleVO[]>('/sys/business-exception/level-rule/list', {})
}

export function saveBusinessExceptionLevelRule(data: BusinessExceptionLevelRuleSaveRequest) {
  return request.post<BusinessExceptionLevelRuleVO>('/sys/business-exception/level-rule/save', data)
}

export function deleteBusinessExceptionLevelRule(id: string) {
  return request.post<boolean>('/sys/business-exception/level-rule/delete', { id })
}

function operate(path: string, data: BusinessExceptionOperationRequest) {
  return request.post<BusinessExceptionDetail>(`/sys/business-exception/${path}`, data)
}

export const assignBusinessException = (data: BusinessExceptionOperationRequest) => operate('assign', data)
export const batchAssignBusinessException = (data: BusinessExceptionBatchAssignRequest) => request.post<BusinessExceptionBatchAssignResult[]>('/sys/business-exception/batch-assign', data)
export const startBusinessException = (data: BusinessExceptionOperationRequest) => operate('start', data)
export const appendBusinessExceptionResult = (data: BusinessExceptionOperationRequest) => operate('append-result', data)
export const resolveBusinessException = (data: BusinessExceptionOperationRequest) => operate('resolve', data)
export const verifyBusinessException = (data: BusinessExceptionOperationRequest) => operate('verify', data)
export const returnBusinessException = (data: BusinessExceptionOperationRequest) => operate('return', data)
export const closeBusinessException = (data: BusinessExceptionOperationRequest) => operate('close', data)
